<template lang="pug">
.rich-text-editor
  .editor-header
    h3 {{ isCreating ? 'New Rich Text' : 'Rich Text' }}

  .editor-form
    el-form(:model="formData" label-position="top")
      el-form-item(label="Heading")
        QuillEditor(
          v-model:content="formData.heading"
          content-type="html"
          :toolbar="headingToolbarOptions"
          placeholder="Enter heading..."
        )

      el-form-item(label="Text")
        QuillEditor(
          v-model:content="formData.text"
          content-type="html"
          :toolbar="toolbarOptions"
          placeholder="Enter your rich text content here..."
        )

  // Action buttons
  .editor-actions
    template(v-if="isCreating")
      el-button(
        @click="$emit('cancel')"
        size="large"
      ) Cancel
      el-button(
        type="primary"
        @click="handleSave"
        size="large"
        :loading="saving"
        :disabled="!formData.heading && !formData.text"
      ) Save Block

    // Save button for editing existing blocks
    template(v-else)
      el-button(
        type="primary"
        @click="handleSave"
        size="large"
        :loading="saving"
        :disabled="!formData.heading && !formData.text || !hasChanges"
      ) Save Changes
</template>

<style scoped>
.editor-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.editor-header h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.editor-header p {
  margin: 0;
  font-size: 14px;
  color: #606266;
}

.editor-form {
  max-width: 500px;
}

.editor-form .el-form-item {
  margin-bottom: 24px;
}

.editor-form .el-form-item__label {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* Rich text editor specific styles */
.rich-text-editor :deep(.ql-container) {
  min-height: 150px;
  font-family: inherit;
}

.rich-text-editor :deep(.ql-editor) {
  font-size: 14px;
  line-height: 1.5;
}

.rich-text-editor :deep(.ql-toolbar) {
  border-top: 1px solid #dcdfe6;
  border-left: 1px solid #dcdfe6;
  border-right: 1px solid #dcdfe6;
}

.rich-text-editor :deep(.ql-container) {
  border-bottom: 1px solid #dcdfe6;
  border-left: 1px solid #dcdfe6;
  border-right: 1px solid #dcdfe6;
}

/* Heading rich text editor specific styles */
.editor-form .el-form-item:first-child :deep(.ql-container) {
  min-height: 60px;
}

.editor-form .el-form-item:first-child :deep(.ql-editor) {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
}

.editor-form .el-form-item:first-child :deep(.ql-toolbar) {
  padding: 8px;
}

.editor-form .el-form-item:first-child :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #c0c4cc;
}
</style>

<script>
import {
  defineComponent, reactive, watch, ref, computed,
} from 'vue';
import { QuillEditor, Quill } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '../../../../../assets/styles/qill.scss';
import useStaticPageUtils from '../shared/index.ts';

// Register custom alignment format to include 'left' option
const Align = Quill.import('attributors/style/align');
Align.whitelist = ['left', 'center', 'right', 'justify'];
Quill.register(Align, true);

// Set icon for left alignment
const Icons = Quill.import('ui/icons');
Icons.align.left = Icons.align[''];

export default defineComponent({
  name: 'RichTextBlock',
  components: {
    QuillEditor,
  },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateRichTextBlock } = useStaticPageUtils();
    const formData = reactive({
      heading: '',
      text: '',
    });
    const saving = ref(false);
    const originalData = reactive({
      heading: '',
      text: '',
    });
    const justSaved = ref(false);

    // Quill toolbar configuration for heading (simpler options)
    const headingToolbarOptions = [
      ['bold', 'italic'],
      [{ align: 'left' }, { align: 'center' }, { align: 'right' }],
    ];

    // Quill toolbar configuration with basic formatting options for text
    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      [{ align: 'left' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
      ['link'],
    ];

    // Track if there are changes to enable/disable save button
    const hasChanges = computed(() => formData.heading !== originalData.heading
      || formData.text !== originalData.text);

    // Initialize form data when block prop changes
    watch(
      () => props.block,
      (newBlock) => {
        // Skip updating form data if we just saved to prevent overwriting user changes
        if (justSaved.value) {
          justSaved.value = false;
          return;
        }

        if (newBlock && newBlock.details) {
          // Store both current form data and original data
          const heading = newBlock.details.heading || '';
          const text = newBlock.details.text || '';

          formData.heading = heading;
          formData.text = text;

          // Store original values to detect changes
          originalData.heading = heading;
          originalData.text = text;
        } else {
          formData.heading = '';
          formData.text = '';
          originalData.heading = '';
          originalData.text = '';
        }
      },
      { immediate: true },
    );

    async function handleSave() {
      try {
        saving.value = true;
        const input = {
          heading: formData.heading || null,
          text: formData.text || null,
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
        };

        const result = await createOrUpdateRichTextBlock(input, props.staticPageData);

        // Set flag to prevent watch from overwriting form data after save
        justSaved.value = true;

        // Update original data after successful save to reset change detection
        if (!props.isCreating) {
          originalData.heading = formData.heading;
          originalData.text = formData.text;
        }

        emit('save', result);
      } catch (error) {
        console.error('Error saving rich text block:', error);
        // TODO: Show error message to user
      } finally {
        saving.value = false;
      }
    }

    return {
      formData,
      saving,
      hasChanges,
      handleSave,
      headingToolbarOptions,
      toolbarOptions,
    };
  },
});
</script>
